/**
 * testDefinitionModel.ts
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — PURE view-model logic
 * for the rebuilt Test Catalogue page. No React, no MUI, no API calls —
 * unit-tested by src/__tests__/testDefinitionModel.test.ts.
 *
 * Carries:
 *   - status derivation (flags + attention rank) for the left list's chips
 *   - list filter/sort + per-chip counts (B1)
 *   - the ONE-form draft: seed from a TestDefinitionItem, dirty detection,
 *     and payload assembly (dirty-diff PUT / create POST — B2/B3)
 *
 * Rename cascade (D2): the FE never sends taskType.Name. When the catalog
 * TestName changes, the BACKEND renames the same-named primary task type to
 * match; sending an explicit taskType.Name would suppress that cascade.
 */

import type {
  CatalogPatch,
  CreateDefinitionRequest,
  TaskTypePatch,
  TestDefinitionItem,
  UpdateDefinitionRequest,
} from '../../../services/testDefinitionService';
import type { TaskRunMode } from '../../../utils/runMode';

// ── Status derivation ─────────────────────────────────────────────────────────

export interface DefinitionFlags {
  /** No active recipe row → quotable but generates ZERO schedule tasks. */
  noRecipe: boolean;
  /** Primary task type lacks machines (CapabilityCount 0) or DefaultMinutes. */
  needsMachineTime: boolean;
  /** Lab mapping exists but awaits lab confirmation. */
  unconfirmedMapping: boolean;
  /** No lab test maps here (map row or exact name) — WO lines can't resolve it. */
  unmapped: boolean;
  inactive: boolean;
}

export function deriveDefinitionFlags(item: TestDefinitionItem): DefinitionFlags {
  return {
    noRecipe: item.recipeSize === 0,
    needsMachineTime:
      item.primaryTaskType !== null
      && (item.primaryTaskType.CapabilityCount === 0
        || item.primaryTaskType.DefaultMinutes == null),
    unconfirmedMapping: item.mappingStatus === 'unconfirmed',
    unmapped: item.mappingStatus === 'unmapped',
    inactive: !item.IsActive,
  };
}

/** True when the (active) item needs admin attention — sorts to the top. */
export function hasProblem(flags: DefinitionFlags): boolean {
  return flags.noRecipe || flags.needsMachineTime || flags.unconfirmedMapping || flags.unmapped;
}

/**
 * Attention-first sort rank: active problems, then clean active rows, then
 * inactive (retired) rows at the bottom.
 */
export function definitionRank(item: TestDefinitionItem): number {
  const flags = deriveDefinitionFlags(item);
  if (flags.inactive) return 2;
  return hasProblem(flags) ? 0 : 1;
}

// ── Left-column list: filter + sort + counts (B1) ────────────────────────────

export type DefinitionListFilter =
  | 'all'
  | 'noRecipe'
  | 'needsMachineTime'
  | 'unconfirmed'
  | 'unmapped'
  | 'inactive';

/**
 * Chip predicate. 'all' and the problem chips cover ACTIVE items; retired
 * items live behind the dedicated 'inactive' chip (matching the old page,
 * which never listed them at all).
 */
export function matchesFilter(item: TestDefinitionItem, filter: DefinitionListFilter): boolean {
  const flags = deriveDefinitionFlags(item);
  switch (filter) {
    case 'all': return !flags.inactive;
    case 'noRecipe': return !flags.inactive && flags.noRecipe;
    case 'needsMachineTime': return !flags.inactive && flags.needsMachineTime;
    case 'unconfirmed': return !flags.inactive && flags.unconfirmedMapping;
    case 'unmapped': return !flags.inactive && flags.unmapped;
    case 'inactive': return flags.inactive;
  }
}

/** Search over name / ASTM / BST / category (case-insensitive substring). */
export function matchesSearch(item: TestDefinitionItem, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  return [item.TestName, item.AstmCode, item.BstCode, item.Category]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

/**
 * Filter (search + category select + status chip) and sort: problems first,
 * then Category / TestName within each rank.
 */
export function filterAndSortDefinitions(
  items: TestDefinitionItem[],
  search: string,
  category: string,
  filter: DefinitionListFilter,
): TestDefinitionItem[] {
  return items
    .filter((i) =>
      matchesFilter(i, filter)
      && (category === '' || i.Category === category)
      && matchesSearch(i, search))
    .sort((a, b) =>
      definitionRank(a) - definitionRank(b)
      || (a.Category ?? 'zz').localeCompare(b.Category ?? 'zz')
      || a.TestName.localeCompare(b.TestName));
}

/** Count per filter chip (search/category-independent — dataset totals). */
export function countByFilter(
  items: TestDefinitionItem[],
): Record<DefinitionListFilter, number> {
  const counts: Record<DefinitionListFilter, number> = {
    all: 0, noRecipe: 0, needsMachineTime: 0, unconfirmed: 0, unmapped: 0, inactive: 0,
  };
  for (const item of items) {
    const flags = deriveDefinitionFlags(item);
    if (flags.inactive) { counts.inactive++; continue; }
    counts.all++;
    if (flags.noRecipe) counts.noRecipe++;
    if (flags.needsMachineTime) counts.needsMachineTime++;
    if (flags.unconfirmedMapping) counts.unconfirmed++;
    if (flags.unmapped) counts.unmapped++;
  }
  return counts;
}

// ── The ONE-form draft (B2/B3) ────────────────────────────────────────────────

/**
 * Editable draft: catalog fields + primary-task-type fields as ONE object.
 * Numerics are held as raw strings (TextField-friendly); '' = null/blank.
 */
export interface DefinitionDraft {
  // Catalog (commercial)
  TestName: string;
  AstmCode: string;
  BstCode: string;
  Category: string;
  Unit: string;
  UnitPriceCad: string;
  UnitPriceUsd: string;
  DefaultQuantity: string;
  Description: string;
  LabNote: string;
  Assumptions: string;
  DefaultMachineKey: string;
  IncludeInSchedule: boolean;
  /** Migration 118: machine | people (effort line) | none. Kept converged with IncludeInSchedule. */
  SchedulingMode: 'machine' | 'people' | 'none';
  // Primary task type (operational)
  ttSubcategory: string;
  ttLabSection: string;
  ttRunMode: TaskRunMode;
  ttAttendedMinutes: string;
  ttPresetDurationDays: string;
  ttDefaultMachineCount: string;
  ttDefaultMinutes: string;
  ttSkillKey: string;
  /** Index Tests Phase 1 / I1: pools this test on the shared virtual Index machine (tests/day) instead of minutes. Edit-mode only (I5). */
  ttIsIndexTest: boolean;
  /** Index Tests Phase 1 / I4: tests/day capacity, held as a raw string ('' = null). Visible only when ttIsIndexTest. */
  ttIndexTestsPerDay: string;
}

export const BLANK_DRAFT: DefinitionDraft = {
  TestName: '',
  AstmCode: '',
  BstCode: '',
  Category: '',
  Unit: 'Each',
  UnitPriceCad: '0',
  UnitPriceUsd: '',
  DefaultQuantity: '1',
  Description: '',
  LabNote: '',
  Assumptions: '',
  DefaultMachineKey: '',
  IncludeInSchedule: true,
  SchedulingMode: 'machine',
  ttSubcategory: '',
  ttLabSection: '',
  ttRunMode: 'attended',
  ttAttendedMinutes: '',
  ttPresetDurationDays: '1',
  ttDefaultMachineCount: '1',
  ttDefaultMinutes: '',
  ttSkillKey: '',
  ttIsIndexTest: false,
  ttIndexTestsPerDay: '',
};

/** Seed the draft from a loaded item (edit mode) or blank (create mode). */
export function draftFromItem(item: TestDefinitionItem | null): DefinitionDraft {
  if (!item) return { ...BLANK_DRAFT };
  const tt = item.primaryTaskType;
  return {
    TestName: item.TestName ?? '',
    AstmCode: item.AstmCode ?? '',
    BstCode: item.BstCode ?? '',
    Category: item.Category ?? '',
    Unit: item.Unit ?? 'Each',
    UnitPriceCad: String(item.UnitPriceCad ?? 0),
    UnitPriceUsd: item.UnitPriceUsd != null ? String(item.UnitPriceUsd) : '',
    DefaultQuantity: String(item.DefaultQuantity ?? 1),
    Description: item.Description ?? '',
    LabNote: item.LabNote ?? '',
    Assumptions: item.Assumptions ?? '',
    DefaultMachineKey: item.DefaultMachineKey ?? '',
    IncludeInSchedule: item.IncludeInSchedule ?? true,
    SchedulingMode: item.SchedulingMode ?? ((item.IncludeInSchedule ?? true) ? 'machine' : 'none'),
    ttSubcategory: tt?.Subcategory ?? '',
    ttLabSection: tt?.LabSection ?? '',
    ttRunMode: tt?.RunMode ?? 'attended',
    ttAttendedMinutes: tt?.AttendedMinutes != null ? String(tt.AttendedMinutes) : '',
    ttPresetDurationDays: String(tt?.PresetDurationDays ?? 1),
    ttDefaultMachineCount: String(tt?.DefaultMachineCount ?? 1),
    ttDefaultMinutes: tt?.DefaultMinutes != null ? String(tt.DefaultMinutes) : '',
    ttSkillKey: tt?.SkillKey ?? '',
    ttIsIndexTest: tt?.IsIndexTest ?? false,
    ttIndexTestsPerDay: tt?.IndexTestsPerDay != null ? String(tt.IndexTestsPerDay) : '',
  };
}

// ── Draft → payload assembly ──────────────────────────────────────────────────

const trimOrNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
const intOrNull = (s: string): number | null => (s.trim() === '' ? null : parseInt(s, 10));
const decOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const numOr = (s: string, fallback: number): number => {
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Catalog-side dirty diff: only fields whose normalized draft value differs
 * from the loaded item are included (a minimal allowlisted PUT patch).
 */
export function buildCatalogPatch(
  draft: DefinitionDraft,
  item: TestDefinitionItem,
): CatalogPatch {
  const patch: CatalogPatch = {};
  const name = draft.TestName.trim();
  if (name !== item.TestName) patch.TestName = name;
  if (trimOrNull(draft.AstmCode) !== (item.AstmCode ?? null)) patch.AstmCode = trimOrNull(draft.AstmCode);
  if (trimOrNull(draft.BstCode) !== (item.BstCode ?? null)) patch.BstCode = trimOrNull(draft.BstCode);
  if (trimOrNull(draft.Category) !== (item.Category ?? null)) patch.Category = trimOrNull(draft.Category);
  if (draft.Unit !== item.Unit) patch.Unit = draft.Unit;
  if (numOr(draft.UnitPriceCad, 0) !== item.UnitPriceCad) patch.UnitPriceCad = numOr(draft.UnitPriceCad, 0);
  const usd = draft.UnitPriceUsd.trim() === '' ? null : numOr(draft.UnitPriceUsd, 0);
  if (usd !== (item.UnitPriceUsd ?? null)) patch.UnitPriceUsd = usd;
  const qty = numOr(draft.DefaultQuantity, 1);
  if (qty !== item.DefaultQuantity) patch.DefaultQuantity = qty;
  if (trimOrNull(draft.Description) !== (item.Description ?? null)) patch.Description = trimOrNull(draft.Description);
  if (trimOrNull(draft.LabNote) !== (item.LabNote ?? null)) patch.LabNote = trimOrNull(draft.LabNote);
  if (trimOrNull(draft.Assumptions) !== (item.Assumptions ?? null)) patch.Assumptions = trimOrNull(draft.Assumptions);
  // GUID casing differs between endpoints — compare case-insensitively.
  const machine = draft.DefaultMachineKey || null;
  const existingMachine = item.DefaultMachineKey ?? null;
  if ((machine ?? '').toLowerCase() !== (existingMachine ?? '').toLowerCase()) {
    patch.DefaultMachineKey = machine;
  }
  // 118: SchedulingMode supersedes the old Include checkbox — the BE keeps
  // IncludeInSchedule converged, so only the mode ships in the patch.
  const existingMode = item.SchedulingMode ?? ((item.IncludeInSchedule ?? true) ? 'machine' : 'none');
  if (draft.SchedulingMode !== existingMode) {
    patch.SchedulingMode = draft.SchedulingMode;
  }
  return patch;
}

/**
 * Task-type-side dirty diff against the item's primary task type. NEVER
 * emits Name — the backend's rename cascade (D2) owns the task-type name.
 * Returns null when the item has no primary task type or nothing changed.
 */
export function buildTaskTypePatch(
  draft: DefinitionDraft,
  item: TestDefinitionItem,
): TaskTypePatch | null {
  const tt = item.primaryTaskType;
  if (!tt) return null;
  const patch: TaskTypePatch = {};
  if (trimOrNull(draft.ttSubcategory) !== (tt.Subcategory ?? null)) {
    patch.Subcategory = trimOrNull(draft.ttSubcategory);
  }
  if ((draft.ttLabSection || null) !== (tt.LabSection ?? null)) {
    patch.LabSection = draft.ttLabSection || null;
  }
  if (draft.ttRunMode !== tt.RunMode) patch.RunMode = draft.ttRunMode;
  // AttendedMinutes only applies to continuous mode; flipping back to
  // attended clears it so a stale value doesn't linger.
  const attended = draft.ttRunMode === 'continuous' ? intOrNull(draft.ttAttendedMinutes) : null;
  if (attended !== (tt.AttendedMinutes ?? null)) patch.AttendedMinutes = attended;
  const preset = intOrNull(draft.ttPresetDurationDays) ?? tt.PresetDurationDays;
  if (preset !== tt.PresetDurationDays) patch.PresetDurationDays = preset;
  const machineCount = intOrNull(draft.ttDefaultMachineCount) ?? tt.DefaultMachineCount;
  if (machineCount !== tt.DefaultMachineCount) patch.DefaultMachineCount = machineCount;
  if (intOrNull(draft.ttDefaultMinutes) !== (tt.DefaultMinutes ?? null)) {
    patch.DefaultMinutes = intOrNull(draft.ttDefaultMinutes);
  }
  if ((draft.ttSkillKey || null) !== (tt.SkillKey ?? null)) {
    patch.SkillKey = draft.ttSkillKey || null;
  }
  if (draft.ttIsIndexTest !== tt.IsIndexTest) patch.IsIndexTest = draft.ttIsIndexTest;
  // Capacity is meaningless (and hidden) once the flag is off — clear it, same
  // as AttendedMinutes clearing when RunMode leaves 'continuous'.
  const indexCap = draft.ttIsIndexTest ? decOrNull(draft.ttIndexTestsPerDay) : null;
  if (indexCap !== (tt.IndexTestsPerDay ?? null)) patch.IndexTestsPerDay = indexCap;
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * The unified Save payload (B2): { catalog: dirty-diff, taskType: dirty-diff
 * | null }. The rename cascade rides on catalog.TestName alone.
 */
export function buildUpdatePayload(
  draft: DefinitionDraft,
  item: TestDefinitionItem,
): UpdateDefinitionRequest {
  return {
    catalog: buildCatalogPatch(draft, item),
    taskType: buildTaskTypePatch(draft, item),
  };
}

/** Dirty check: anything to send? (Save stays disabled until true.) */
export function isDraftDirty(draft: DefinitionDraft, item: TestDefinitionItem | null): boolean {
  if (!item) {
    // Create mode: dirty once any field differs from the blank draft.
    return JSON.stringify(draft) !== JSON.stringify(BLANK_DRAFT);
  }
  const payload = buildUpdatePayload(draft, item);
  return Object.keys(payload.catalog).length > 0 || payload.taskType !== null;
}

/**
 * Create payload (B3): full catalog body + (when the "also create matching
 * task type" switch is ON) the scheduling fields as taskType overrides. The
 * task type NAME is never sent — the backend names it after the catalog item
 * (or links a same-named active one).
 */
export function buildCreatePayload(
  draft: DefinitionDraft,
  createTaskType: boolean,
): CreateDefinitionRequest {
  const body: CreateDefinitionRequest = {
    catalog: {
      TestName: draft.TestName.trim(),
      AstmCode: trimOrNull(draft.AstmCode),
      BstCode: trimOrNull(draft.BstCode),
      Category: trimOrNull(draft.Category),
      Unit: draft.Unit || 'Each',
      UnitPriceCad: numOr(draft.UnitPriceCad, 0),
      UnitPriceUsd: draft.UnitPriceUsd.trim() === '' ? null : numOr(draft.UnitPriceUsd, 0),
      DefaultQuantity: intOrNull(draft.DefaultQuantity) ?? 1,
      Description: trimOrNull(draft.Description),
      LabNote: trimOrNull(draft.LabNote),
      Assumptions: trimOrNull(draft.Assumptions),
      DefaultMachineKey: draft.DefaultMachineKey || null,
      IncludeInSchedule: draft.SchedulingMode !== 'none',
      SchedulingMode: draft.SchedulingMode,
    },
    createTaskType,
  };
  if (createTaskType) {
    body.taskType = {
      Subcategory: trimOrNull(draft.ttSubcategory),
      LabSection: draft.ttLabSection || null,
      RunMode: draft.ttRunMode,
      AttendedMinutes: draft.ttRunMode === 'continuous' ? intOrNull(draft.ttAttendedMinutes) : null,
      PresetDurationDays: intOrNull(draft.ttPresetDurationDays) ?? 1,
      DefaultMachineCount: intOrNull(draft.ttDefaultMachineCount) ?? 1,
      DefaultMinutes: intOrNull(draft.ttDefaultMinutes),
      SkillKey: draft.ttSkillKey || null,
    };
  }
  return body;
}

// ── Validation (mirrors the backend 400s) ─────────────────────────────────────

const isWholeNumber = (s: string): boolean => /^\d+$/.test(s.trim());

/**
 * Client-side validation before Save. Returns an error message or null.
 * `capabilityCount` feeds the Run-Mode-Phase-B continuous guard: a continuous
 * task type needs resolvable machine minutes (DefaultMinutes or >=1 capability).
 */
export function validateDraft(
  draft: DefinitionDraft,
  opts: { editsTaskType: boolean; capabilityCount: number },
): string | null {
  const name = draft.TestName.trim();
  if (!name) return 'Test name is required';
  if (name.length > 250) return 'Test name must be 250 characters or fewer';
  if (draft.UnitPriceCad.trim() !== '' && numOr(draft.UnitPriceCad, -1) < 0) {
    return 'Unit price (CAD) must be 0 or more';
  }
  if (!opts.editsTaskType) return null;

  if (draft.ttPresetDurationDays.trim() !== ''
    && (!isWholeNumber(draft.ttPresetDurationDays) || parseInt(draft.ttPresetDurationDays, 10) < 1)) {
    return 'Preset duration must be a whole number of days (1 or more)';
  }
  if (draft.ttDefaultMachineCount.trim() !== ''
    && (!isWholeNumber(draft.ttDefaultMachineCount) || parseInt(draft.ttDefaultMachineCount, 10) < 1)) {
    return 'Machine quantity must be a whole number (1 or more)';
  }
  if (draft.ttDefaultMinutes.trim() !== '' && !isWholeNumber(draft.ttDefaultMinutes)) {
    return 'Default minutes must be a whole number (or blank)';
  }
  if (draft.ttRunMode === 'continuous') {
    if (draft.ttAttendedMinutes.trim() !== '') {
      const att = parseInt(draft.ttAttendedMinutes, 10);
      if (!isWholeNumber(draft.ttAttendedMinutes) || att < 0 || att > 1440) {
        return 'Attended minutes must be between 0 and 1440 (or blank)';
      }
    }
    // Run Mode Phase B invariant: continuous needs resolvable machine minutes.
    if (draft.ttDefaultMinutes.trim() === '' && opts.capabilityCount === 0) {
      return 'A continuous (24/7) test needs machine minutes: set Default Minutes '
        + 'or add a machine under Machines & Time first.';
    }
  }
  if (draft.ttIsIndexTest && draft.ttIndexTestsPerDay.trim() !== '') {
    const cap = Number(draft.ttIndexTestsPerDay);
    if (!Number.isFinite(cap) || cap <= 0) {
      return 'Tests per day must be a number greater than 0';
    }
  }
  return null;
}
